import { gql, useMutation, useQuery } from '@apollo/client';
import { useState, useEffect, useCallback, useRef } from 'react';

// Mutation per chiamare la funzione verifica_valori_ddt_e_salva
// Wrapper VOLATILE per Hasura che richiama verifica_valori_ddt e salva i dati
// Riceve un singolo parametro jsonb p_input
const VERIFICA_VALORI_DDT_E_SALVA = gql`
  mutation VerificaValoriDdtESalva($input: jsonb = {}) {
    ghiaia3_verifica_valori_ddt_e_salva(args: {p_input: $input}) {
      result
    }
  }
`;

// Query per polling pesi dalla pesa
const PESI_PONTE = gql`
  query PesiPonte($cod_bolla_pesa: String!) {
    pesiponte(
      where: { cod_bolla: { _eq: $cod_bolla_pesa } }
      order_by: { data: desc }
      limit: 10
    ) {
      peso
      data
    }
  }
`;

// Hook per verifica_valori_ddt_e_salva
export function useVerificaValoriDdt() {
  const [mutate, { loading, error }] = useMutation(VERIFICA_VALORI_DDT_E_SALVA);

  const verificaDdt = useCallback(async (values) => {
    try {
      // Costruisce l'oggetto jsonb per la funzione PostgreSQL
      // Usa ?? (nullish coalescing) per preservare valori 0 e stringa vuota
      const p_input = {
        cod_bolla: values.cod_bolla ?? null,
        num_bolla: values.num_bolla ?? null,
        data_bolla: values.data_bolla ?? null,
        rag_soc: values.rag_soc ?? null,
        targa: values.targa ?? null,
        rag_soc_vettore: values.rag_soc_vettore ?? null,
        vett_ns: values.vett_ns ?? false,
        rag_soc_conducente: values.rag_soc_conducente ?? null,
        cod_commessa: values.cod_commessa ?? null,
        data_inizio: values.data_inizio ?? null,
        cod_artic: values.cod_artic ?? null,
        destinazione: values.destinazione ?? null,
        id_dest: values.id_dest ?? null,
        destinazione_pattern: values.destinazione_pattern ?? null,
        lordo: values.lordo ?? null,
        tara: values.tara ?? null,
        pesospec: values.pesospec ?? null,
        num_silos: values.num_silos ?? null,
        temperatura: values.temperatura ?? null,
        cig: values.cig ?? null,
        cup: values.cup ?? null,
        stampante: values.stampante ?? null,
        stamparescontrino: values.stamparescontrino ?? null,
        conferma: values.conferma ?? false,
        // Modalita' coupon (template ddtcoupon) e dati specifici del coupon
        // (vedi SPECS_COUPON.md in ghiaia3sql e SPECCOUPON2026.md)
        modalita_coupon: values.modalita_coupon ?? false,
        cellulare: values.cellulare ?? null,
        dtara: values.dtara ?? 0,
        dlordo: values.dlordo ?? 0,
      };

      const response = await mutate({ variables: { input: p_input } });
      // La funzione ritorna SETOF con colonna jsonb 'result'
      const rows = response.data.ghiaia3_verifica_valori_ddt_e_salva;
      if (rows && rows.length > 0) {
        return rows[0].result;
      }
      return null;
    } catch (err) {
      console.error('Errore verifica DDT:', err);
      throw err;
    }
  }, [mutate]);

  return { verificaDdt, loading, error };
}

// Hook per polling pesi dalla pesa
export function usePesiPonte(codBollaPesa) {
  const [pesi, setPesi] = useState([]);
  const [pollingInterval, setPollingInterval] = useState(10000); // Default 10s

  const { data, startPolling, stopPolling } = useQuery(PESI_PONTE, {
    variables: { cod_bolla_pesa: codBollaPesa || 'XX' },
    pollInterval: pollingInterval,
    skip: !codBollaPesa,
  });

  useEffect(() => {
    if (data?.pesiponte) {
      setPesi(data.pesiponte);
      
      // Adatta intervallo polling
      const hasPeso = data.pesiponte.some(p => p.peso > 0);
      const newInterval = hasPeso ? 2000 : 10000;
      
      if (newInterval !== pollingInterval) {
        setPollingInterval(newInterval);
        stopPolling();
        startPolling(newInterval);
      }
    }
  }, [data, pollingInterval, startPolling, stopPolling]);

  return { pesi, pollingInterval };
}

// Query per caricare una bolla esistente (per "ripeti bolla simile")
const BOLLA_BY_PK = gql`
  query BollaByPk($anno: Int!, $cod_bolla: bpchar!, $num_bolla: Int!) {
    bolle_by_pk(anno: $anno, cod_bolla: $cod_bolla, num_bolla: $num_bolla) {
      cod_bolla
      cig
      cup
      vett_ns
      anagrafe { rag_soc: rag_soc }
      anagvettore { rag_soc: rag_soc }
      anagconducente { rag_soc: rag_soc }
      veicoli { targa }
      destinazioni { destinazione id_dest }
      bolle_commesse_trasporti {
        data_inizio
        destinazione_pattern
      }
      bolle_vocis(where: {num_voce: {_eq: "1"}}) {
        pesospec
        temperatura
        articolo { cod_artic }
        bolle_commesse_prodotti { cod_commessa data_inizio }
      }
    }
  }
`;

// Hook per caricare una bolla esistente e mappare i dati ai parametri URL di DDT2026
// Ritorna { bollaParams, loading, error } dove bollaParams è un oggetto con i searchParams da settare
export function useBollaByPk(anno, codBolla, numBolla) {
  const skip = !anno || !codBolla || !numBolla;

  const { data, loading, error } = useQuery(BOLLA_BY_PK, {
    variables: { anno: parseInt(anno, 10), cod_bolla: codBolla, num_bolla: parseInt(numBolla, 10) },
    skip,
    fetchPolicy: 'network-only',
  });

  const bollaParams = data?.bolle_by_pk ? mapBollaToParams(data.bolle_by_pk) : null;
  return { bollaParams, loading: loading && !skip, error };
}

function mapBollaToParams(b) {
  const voce = b.bolle_vocis?.[0];
  const commessaProd = voce?.bolle_commesse_prodotti;
  const commessaTrasporti = b.bolle_commesse_trasporti;
  const params = {};

  const set = (key, val) => {
    if (val !== null && val !== undefined && val !== '') params[key] = String(val);
  };

  set('cod_bolla', b.cod_bolla);
  set('rag_soc', b.anagrafe?.rag_soc);
  // targa, conducente e vettore esclusi: vanno sempre richiesti all'utente
  set('cod_commessa', commessaProd?.cod_commessa);
  set('data_inizio', commessaProd?.data_inizio);
  set('cod_artic', voce?.articolo?.cod_artic);
  set('destinazione', b.destinazioni?.destinazione);
  set('id_dest', b.destinazioni?.id_dest);
  set('destinazione_pattern', commessaTrasporti?.destinazione_pattern);
  set('cig', b.cig);
  set('cup', b.cup);
  set('pesospec', voce?.pesospec);
  set('temperatura', voce?.temperatura);

  return params;
}

// Query per polling stato stampa dalla coda ddt_print_queue
const PRINT_STATUS = gql`
  query PrintStatus($cod_bolla: bpchar, $num_bolla: Int, $treg: timestamptz) {
    ddt_print_queue(where: {cod_bolla: {_eq: $cod_bolla}, num_bolla: {_eq: $num_bolla}, treg: {_eq: $treg}}) {
      esito_stampa
    }
  }
`;

// Hook per polling stato stampa
// savedDDT: { cod_bolla, num_bolla, treg } dalla riga inserita
// Ritorna { msgPrint, polling }
export function usePrintStatus(savedDDT) {
  const [msgPrint, setMsgPrint] = useState(null);
  const doneRef = useRef(false);

  const shouldPoll = Boolean(savedDDT && !doneRef.current);

  useQuery(PRINT_STATUS, {
    variables: savedDDT || {},
    skip: !shouldPoll,
    pollInterval: shouldPoll ? 2000 : 0,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const row = data?.ddt_print_queue?.[0];
      if (row?.esito_stampa) {
        setMsgPrint(row.esito_stampa);
        doneRef.current = true;
      }
    },
    onError: (error) => {
      setMsgPrint(`Errore: ${error.message}`);
      doneRef.current = true;
    },
  });

  return { msgPrint, polling: shouldPoll && !doneRef.current };
}
